// # Database Manager for Master Database
import { db } from '../../../../firebase/config';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  collection
} from 'firebase/firestore';
// # No need for Excel/CSV parsing libraries here anymore

class DbManager {
  // # Get collection reference based on step
  static getCollectionName(step) {
    return `mdb_${step.toLowerCase().replace(/\s+/g, '')}`;
  }

  // # Load chapter tasks
  static async loadChapterTasks(step, chapterId) {
    try {
      const startTime = performance.now();
      
      // # Get document reference
      const collectionName = this.getCollectionName(step);
      const docRef = doc(db, collectionName, chapterId);
      
      // # Get document data
      const docSnap = await getDoc(docRef);
      
      console.log('Master Database - Loading chapter tasks:', {
        operation: 'load_chapter_tasks',
        step,
        chapterId,
        exists: docSnap.exists(),
        timestamp: new Date().toISOString(),
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // # Convert tasks object to array
        const tasks = data.tasks ? Object.values(data.tasks) : [];
        return {
          success: true,
          data: tasks,
          metadata: data.metadata || {}
        };
      } else {
        // # Create empty document if it doesn't exist
        await setDoc(docRef, {
          metadata: {
            lastUpdated: serverTimestamp(),
            totalTasks: 0,
            chapterName: chapterId
          },
          tasks: {}
        });
        return {
          success: true,
          data: [],
          metadata: {
            lastUpdated: new Date(),
            totalTasks: 0,
            chapterName: chapterId
          }
        };
      }
    } catch (error) {
      console.error('Error loading chapter tasks:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // # Save chapter tasks
  static async saveChapterTasks(step, chapterId, tasks, userId) {
    try {
      // # Get document reference
      const collectionName = this.getCollectionName(step);
      const docRef = doc(db, collectionName, chapterId);
      
      // # Check if document exists
      const docSnap = await getDoc(docRef);
      
      // # Convert tasks array to object with task_id as key
      const tasksObject = {};
      tasks.forEach(task => {
        tasksObject[task.task_id] = {
          ...task,
          metadata: {
            createdAt: docSnap.exists() ? 
              (docSnap.data().tasks?.[task.task_id]?.metadata?.createdAt || serverTimestamp()) : 
              serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: docSnap.exists() ? 
              (docSnap.data().tasks?.[task.task_id]?.metadata?.createdBy || userId) : 
              userId,
            lastEditedBy: userId
          }
        };
      });

      // # Create or update document
      await setDoc(docRef, {
        metadata: {
          lastUpdated: serverTimestamp(),
          totalTasks: tasks.length,
          chapterName: chapterId
        },
        tasks: tasksObject
      }, { merge: true });  // Use merge option to preserve existing data

      return {
        success: true
      };
    } catch (error) {
      console.error('Error saving chapter tasks:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // # Check if collection exists
  static async collectionExists(step) {
    try {
      const collectionName = this.getCollectionName(step);
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDoc(doc(collectionRef, '__dummy__'));
      return snapshot.exists();
    } catch (error) {
      console.error('Error checking collection:', error);
      return false;
    }
  }
}

export default DbManager;
